import React, { useCallback, useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { SideOverlay } from "../../../../components";
import { useStore } from "../../../../helpers";
import { useStyles } from "./request-type-styles";

import {
    defaultServiceRequest,
    REQUEST_TYPES,
    serviceRequestValidationSchema,
} from "./request-type-schema";
import {
    FTPAutoComplete,
    FTPFormButton,
    FTPInput,
    FTPRadioGroup,
} from "../../../../components-new";
import {
    useAddServiceRequestType,
    useEditServiceRequestType,
} from "../../sr-hooks";
import { useParams } from "react-router";
import { useWindowSize } from "react-use";
import { DynamicFieldGroup, SRColorPicker } from "../sr-settings-components";
import { safe_get } from "../../../../report-visuals";
import { VortexSpinner } from "../../../../features/digital-repository";
import { useTranslation } from "react-i18next";

export const AddRequestType = observer((props: any) => {
    const { serviceRequestsStore } = useStore();
    const { projectid } = useParams();
    const { height } = useWindowSize();
    const { activeRequestType } = serviceRequestsStore;
    const { t } = useTranslation("service_request_settings");
    const id = safe_get(activeRequestType, "id", null);

    const methods = useForm({
        defaultValues: defaultServiceRequest,
        // @ts-ignore
        resolver: yupResolver(serviceRequestValidationSchema),
    });

    const { mutate, isLoading, isSuccess } = useAddServiceRequestType();
    const {
        mutate: edit,
        isLoading: isEditing,
        isSuccess: editSuccess,
    } = useEditServiceRequestType();

    const categories = useMemo(() => {
        const values = serviceRequestsStore.meta.type.map(
            (val) => val["type"]["category"]
        );
        return Array.from(new Set(values));
    }, [projectid]);

    const sub_categories = useMemo(() => {
        const values = serviceRequestsStore.meta.type
            .map((val) => val["type"]["subcategory"])
            .filter((val) => typeof val === "string");
        return Array.from(new Set(values));
    }, [projectid]);
    console.log(sub_categories, "sub categories");
    useEffect(() => {
        if (id) {
            methods.reset(activeRequestType.type);
        } else {
            methods.reset({ ...activeRequestType });
        }
    }, [id]);

    const onSubmit = async (data) => {
        if (!id) {
            const payload = {
                projectid: parseInt(projectid),
                default_assignee: null,
                type: data,
                more: {},
                request_type: data.request_type,
            };
            // @ts-ignore
            mutate(payload);
        } else {
            const payload = {
                ...activeRequestType,
                type: data,
            };
            // @ts-ignore
            edit(payload);
        }
    };

    const onError = (error) => {
        console.log(error);
    };

    const closeModal = useCallback(() => {
        setTimeout(() => {
            serviceRequestsStore.setActiveRequestType({});
            methods.reset({ ...defaultServiceRequest });
        }, 1000);
        serviceRequestsStore.update_modal_status("addRequestType", false);
    }, []);

    useEffect(() => {
        if (isSuccess || editSuccess) {
            closeModal();
        }
    }, [editSuccess, isSuccess]);

    return (
        <SideOverlay
            onClose={closeModal}
            show={serviceRequestsStore.modals.addRequestType}
            title={t("request_type_create_service")}
            size={"large"}
        >
            <FormProvider {...methods}>
                <form
                    className="mb-12 flex flex-col gap-y-2 rounded-xl bg-white p-4"
                    onSubmit={methods.handleSubmit(onSubmit, onError)}
                >
                    <FTPRadioGroup
                        name={"request_type"}
                        label={t("priority_type")}
                        options={REQUEST_TYPES}
                    />
                    <div className={"flex flex-col gap-y-4"}>
                        <div
                            className={
                                "grid grid-cols-1 justify-center gap-4 sm:grid-cols-2"
                            }
                        >
                            <FTPInput
                                label={t("request_type_name")}
                                name={"name"}
                            />
                            <SRColorPicker
                                name="color"
                                defaultValue="#F4D1F4"
                            />
                        </div>

                        <FTPAutoComplete
                            name={"category"}
                            label={t("assignee_department")}
                            data={categories}
                            placeholder={t("select_department")}
                            addNewOption={true}
                        />
                        <FTPAutoComplete
                            name={"subcategory"}
                            label={t("ministry")}
                            data={sub_categories}
                            placeholder={t("sub_category")}
                            addNewOption={true}
                        />
                        <DynamicFieldGroup
                            label={t("request_type_display")}
                            name="display"
                        />
                        <DynamicFieldGroup
                            label={t("request_type_description")}
                            name="description"
                        />

                        <FTPAutoComplete
                            name={"tags"}
                            label={t("tags")}
                            data={[
                                "Aadhar Card",
                                "Voter Card",
                                "Ration Card",
                                "Passport",
                                "Not Required",
                            ]}
                            multiple={true}
                            placeholder={t("request_type_tags")}
                        />
                        <FTPAutoComplete
                            name={"required_docs"}
                            label={t("required_documents")}
                            data={[
                                "Aadhar Card",
                                "Voter Card",
                                "Ration Card",
                                "Passport",
                                "Not Required",
                            ]}
                            multiple={true}
                            placeholder={t("select_required_documents")}
                        />
                    </div>
                    <div className="fixed right-0 top-0 flex w-full justify-end gap-x-2 bg-background-default py-2 pr-8 md:hidden">
                        <VortexSpinner visible={isLoading || isEditing} />
                        <FTPFormButton
                            actionType={t("location_cancel")}
                            handleButtonClick={closeModal}
                        />
                        <FTPFormButton
                            type={"submit"}
                            actionType={t("location_save")}
                            handleButtonClick={() => {}}
                        />
                    </div>
                    <div className="fixed bottom-2 right-0 hidden w-full justify-end gap-x-2 bg-background-default py-2 pr-8 md:flex">
                        <VortexSpinner visible={isLoading || isEditing} />
                        <FTPFormButton
                            actionType={t("location_cancel")}
                            handleButtonClick={closeModal}
                        />
                        <FTPFormButton
                            type={"submit"}
                            actionType={t("location_save")}
                            handleButtonClick={() => {}}
                        />
                    </div>
                </form>
            </FormProvider>
        </SideOverlay>
    );
});
